import React, { useEffect, useState } from 'react';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi, uploadApi, uploadUrl } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Camera, X, ClipboardPlus, Check, Ban, CheckCircle2 } from 'lucide-react';
import { fmtMoney, fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { PORTAL } from '@/constants/testIds';

export default function PortalServiceRequestsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', property_id: '', photo_ids: [], photo_urls: [] });

  const load = () => portalApi.serviceRequests()
    .then(setItems)
    .catch(() => {}) // surfaced by the api response interceptor
    .finally(() => setLoaded(true));
  useEffect(() => { load(); portalApi.properties().then(setProperties).catch(() => {}); }, []);

  const attach = async (file) => {
    try {
      const up = await uploadApi.upload(file);
      setForm(f => ({ ...f, photo_ids: [...f.photo_ids, up.id], photo_urls: [...f.photo_urls, up.url] }));
    } catch { toast.error('Upload failed'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await portalApi.createServiceRequest({
        title: form.title, description: form.description,
        property_id: form.property_id || null, photo_ids: form.photo_ids,
      });
      toast.success('Request sent — we’ll quote it shortly.');
      setOpen(false);
      setForm({ title: '', description: '', property_id: '', photo_ids: [], photo_urls: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const accept = async (id) => { await portalApi.acceptServiceRequest(id); toast.success('Accepted — a job has been created'); load(); };
  const decline = async (id) => { await portalApi.declineServiceRequest(id); toast.success('Declined'); load(); };

  return (
    <PortalShell title="Service Requests">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Service Requests</h1>
          <p className="text-sm text-muted-foreground">Ask us for extra services with photos — we’ll send a quick quote.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={PORTAL.newServiceRequest}><Plus className="h-4 w-4 mr-2" />New request</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New service request</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>What do you need? *</Label><Input required placeholder="Porch is dirty, please power-wash" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid={PORTAL.serviceRequestTitle} /></div>
              <div><Label>Details</Label><Textarea rows={3} placeholder="Any additional context…" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid={PORTAL.serviceRequestDescription} /></div>
              <div><Label>Property</Label>
                <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} data-testid={PORTAL.serviceRequestProperty}>
                  <option value="">(none)</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                </select>
              </div>
              <div>
                <Label>Photos</Label>
                <div className="flex flex-wrap gap-2 items-center">
                  {form.photo_urls.map((u, i) => (
                    <div key={i} className="relative">
                      <img src={uploadUrl(u)} alt="attached" className="h-16 w-16 object-cover rounded-md border" />
                      <button type="button" onClick={() => setForm(f => ({ ...f, photo_ids: f.photo_ids.filter((_, x) => x !== i), photo_urls: f.photo_urls.filter((_, x) => x !== i) }))} className="absolute -top-1 -right-1 rounded-full bg-background border p-0.5"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={PORTAL.serviceRequestPhoto} onChange={e => e.target.files?.[0] && attach(e.target.files[0])} />
                    <div className="h-16 w-16 border-2 border-dashed rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground"><Camera className="h-5 w-5" /></div>
                  </label>
                </div>
              </div>
              <DialogFooter><Button type="submit" data-testid={PORTAL.serviceRequestSubmit}>Send</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardPlus} title="No requests yet" description="Snap a photo, describe what you need, and we’ll send a quote right away." />
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold">{r.title}</div>
                    {r.description && <div className="text-sm text-muted-foreground mt-1">{r.description}</div>}
                    <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(r.created_at)}</div>
                    {r.photo_ids && r.photo_ids.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {r.photo_ids.map(pid => <img key={pid} src={uploadUrl(`/uploads/${pid}.jpg`)} alt="" onError={(e) => { e.target.style.display = 'none'; }} className="h-16 w-16 object-cover rounded-md border" />)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant="outline">{r.status}</Badge>
                    {r.quoted_amount != null && <div className="font-semibold tabular-nums">{fmtMoney(r.quoted_amount)}</div>}
                    {r.status === 'quoted' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => decline(r.id)} data-testid={PORTAL.serviceRequestDecline}><Ban className="h-3 w-3 mr-1" />Decline</Button>
                        <Button size="sm" onClick={() => accept(r.id)} data-testid={PORTAL.serviceRequestAccept}><Check className="h-3 w-3 mr-1" />Accept</Button>
                      </div>
                    )}
                    {r.status === 'accepted' && r.converted_job_id && <div className="flex items-center gap-1 text-xs text-[hsl(var(--fc-success))]"><CheckCircle2 className="h-3 w-3" />Job created</div>}
                  </div>
                </div>
                {r.quote_note && <div className="mt-2 text-xs bg-muted rounded-md p-2">{r.quote_note}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PortalShell>
  );
}
